import { useFormContext, type FieldValues } from "react-hook-form";
import { useLingui } from "@lingui/react/macro";
import styles from "./TemplateCustomRecurringInput.module.scss";
import { Text } from "@medoma/components/Text/Text";
import { Plural, Trans } from "@lingui/react/macro";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import InputField from "@medoma/components/InputField/InputField";
import Select from "@medoma/components/Select/Select";
import { customRecurrenceIntervalUnitSchema } from "./schedulingModels";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import { useIsInFinalValidationPhase } from "@/pages/template-manager/TemplateFieldValidationContext";
import type { TemplateCustomRecurringIntervalFields } from "@/pages/template-manager/TemplateForm/templateFormSchema";

export const TemplateCustomRecurringInterval = () => {
  const {
    formState: { errors },
    register,
    watch,
  } = useFormContext<FieldValues>();

  const { t } = useLingui();

  const intervalFieldName =
    useFieldPathWithPrefix<TemplateCustomRecurringIntervalFields>(
      "customRecurrenceInterval",
    );
  const unitFieldName =
    useFieldPathWithPrefix<TemplateCustomRecurringIntervalFields>(
      "customRecurrenceIntervalUnit",
    );
  const intervalError =
    useFieldErrorWithPrefix<TemplateCustomRecurringIntervalFields>(
      errors,
      "customRecurrenceInterval",
    );
  const unitError =
    useFieldErrorWithPrefix<TemplateCustomRecurringIntervalFields>(
      errors,
      "customRecurrenceIntervalUnit",
    );
  const isInFinalValidationPhase = useIsInFinalValidationPhase();

  const interval = watch(intervalFieldName);

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>
        <Trans>Anpassad upprepning</Trans>
      </legend>
      {intervalError ? (
        <div className={styles.errorMessage}>
          <ErrorMessage message={intervalError} weight="bold" />
        </div>
      ) : null}
      {unitError ? (
        <div className={styles.errorMessage}>
          <ErrorMessage message={unitError} weight="bold" />
        </div>
      ) : null}
      <div className={styles.inputWrapper}>
        <Text element="div">
          <Trans
            context={
              "Prefix for custom recurrence interval, e.g. 'Var 3:e vecka' or 'Var 2:a dag'"
            }
          >
            Var
          </Trans>
        </Text>
        <InputField
          label={t`Upprepningsintervall`}
          hideLabel
          showOptionalLabel={false}
          type="number"
          outerWidth="fit"
          {...register(intervalFieldName, {
            required: isInFinalValidationPhase
              ? {
                  value: true,
                  message: t`Ange upprepningsintervall`,
                }
              : undefined,
            min: {
              value: 1,
              message: t`Ange upprepningsintervall som minst 1`,
            },
          })}
        />
        <Select
          label={t`Enhet för upprepningsintervall`}
          hideLabel
          {...register(unitFieldName, {
            required: isInFinalValidationPhase
              ? {
                  value: true,
                  message: t`Välj en enhet för upprepningsintervallet`,
                }
              : undefined,
          })}
        >
          <option value={customRecurrenceIntervalUnitSchema.enum.weeks}>
            <Plural
              value={interval ? interval : "0"}
              _1="vecka"
              other="vecka"
            />
          </option>
          <option value={customRecurrenceIntervalUnitSchema.enum.days}>
            <Plural value={interval ? interval : "0"} _1="dag" other="dag" />
          </option>
        </Select>
      </div>
    </fieldset>
  );
};
